import React, { useState, useEffect } from 'react';
import { RoomEvent } from 'livekit-client';
import {
  LiveKitRoom,
  RoomAudioRenderer,
  useRoomContext,
  useVoiceAssistant,
  BarVisualizer,
} from '@livekit/components-react';
import './App.css';
import CallSummary from './components/CallSummary';
import Avatar from './components/Avatar';

const LIVEKIT_URL = process.env.REACT_APP_LIVEKIT_URL;
const TOKEN_SERVER_URL = process.env.REACT_APP_TOKEN_SERVER_URL || 'http://localhost:5001';

function App() {
  const [token, setToken] = useState('');
  const [connecting, setConnecting] = useState(false);

  const generateToken = async () => {
    setConnecting(true);
    try {
      // Call backend token server
      const roomName = `appointment-${Date.now()}`;
      const participantName = `User-${Math.random().toString(36).substr(2, 9)}`;
      
      // Token server URL (from env or localhost for development)
      const response = await fetch(`${TOKEN_SERVER_URL}/token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          roomName,
          participantName,
        }),
      });
      
      if (!response.ok) {
        throw new Error(`Token generation failed: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Token generated successfully for room:', data.roomName);
      setToken(data.token);
    } catch (error) {
      console.error('Failed to generate token:', error);
      console.error('Token server URL:', TOKEN_SERVER_URL);
      alert(`Failed to connect to backend.\n\nURL: ${TOKEN_SERVER_URL}\nError: ${error.message}\n\nPlease check:\n1. Backend is running on Railway\n2. Environment variable REACT_APP_TOKEN_SERVER_URL is set correctly`);
      setConnecting(false);
    }
  };

  return (
    <div className="App">
      <header className="App-header">
        <h1>🎙️ AI Appointment Assistant</h1>
      </header>

      {!token ? (
        <div className="connect-container">
          <h2>Welcome to the AI Appointment Assistant</h2>
          <p>Click below to start a voice conversation with our AI assistant</p>
          <button 
            className="connect-button"
            onClick={generateToken}
            disabled={connecting}
          >
            {connecting ? 'Connecting...' : 'Start Conversation'}
          </button>
        </div>
      ) : (
        <LiveKitRoom
          serverUrl={LIVEKIT_URL}
          token={token}
          connect={true}
          audio={true}
          video={false}
          onDisconnected={() => setToken('')}
        >
          <ConversationRoom />
        </LiveKitRoom>
      )}
    </div>
  );
}

function ConversationRoom() {
  const [showSummary, setShowSummary] = useState(false);
  const [summary, setSummary] = useState(null);

  const room = useRoomContext();
  const { state: agentState } = useVoiceAssistant();

  useEffect(() => {
    if (!room) return;

    // Listen for data messages from agent (summaries)
    const handleDataReceived = (payload, participant) => {
      try {
        const data = JSON.parse(new TextDecoder().decode(payload));
        
        if (data.type === 'summary') {
          setSummary(data.summary);
          setShowSummary(true);
        }
      } catch (error) {
        console.error('Failed to parse data:', error);
      }
    };

    room.on(RoomEvent.DataReceived, handleDataReceived);

    return () => {
      room.off(RoomEvent.DataReceived, handleDataReceived);
    };
  }, [room]);

  return (
    <div className="conversation-room">
      <div className="main-content">
        <div className="avatar-container">
          <Avatar agentState={agentState} />
          
          <div className="audio-visualizer">
            <BarVisualizer
              state={agentState}
              barCount={5}
              trackRef={undefined}
              options={{ minHeight: 20 }}
            />
          </div>

          <div className="agent-status">
            {agentState === 'listening' && '🎤 Listening...'}
            {agentState === 'thinking' && '🤔 Processing...'}
            {agentState === 'speaking' && '🗣️ Speaking...'}
            {agentState === 'idle' && '💬 Ready'}
          </div>
        </div>
      </div>

      {showSummary && (
        <CallSummary
          summary={summary}
          onClose={() => setShowSummary(false)}
        />
      )}

      <RoomAudioRenderer />
    </div>
  );
}

export default App;
